/**
 * What the object toolbar shows for the selection, in the app's order. The app builds one toolbar for every
 * kind of object (`GUI/Dialogs/ContextToolbar.cs`, `AddButtons`): a fixed row of slots — colour, add, size,
 * duplicate, turn, mirror, remove, replace, «⋯», show wall — each a group of commands of which the first that
 * fits the selection is shown and an empty one takes no room; «⋯» is a list of its own
 * (`GUI/Commands/MorePopupCommand.cs`); under the object a row of text buttons (room type, «Войти в комнату» /
 * «Приблизить»). This module is that choice, per selection and per view: the floor plan is the app's Floor2D,
 * the ceiling plan its Ceiling2D, the wall plan its Wall2D and the scene its 3D. `docs/editor-interface.md`
 * («The object toolbar») lists the result, and what the web leaves out and why.
 *
 * Pure: it reads the state and returns entries with their actions; `ContextBar` draws them and the tests read them.
 */
import { store, type EditorState, type EditScope, type Selection } from '../state/store';
import type { Item, Level, Opening, Room, Surface, Vec2, Wall } from '../core/hp/types';
import { contains } from '../core/geometry/edit';
import { openingKind, paintableOpening } from '../state/openingProperties';
import { takesCurtains } from '../state/curtains';
import { atticFrame, atticPoint, nearestAtticWall, takesAttic } from '../state/attic';
import { knownModelTags } from '../state/modelTags';
import { isRegion } from '../core/hp/regions';
import { isContourWall, isFreeStandingWall, HIDDEN_WALL_OPENING_MAX, roomHasPlinths, roomHasTrims, roomPlinthWalls, type Trim } from '../state/contextCommands';
import { isInteriorSide } from '../state/wallPlan';
import { showsIn3D } from '../state/floors';
import { hasRoomType } from '../state/roomTypes';
import { labelCenter } from '../state/minimap';
import { editorAccess } from '../agent/editorAccess';
import type { IconName } from './Icon';
import { floorArea } from '../core/geometry/roomFloor';
import { t } from '../i18n/I18n';

/** The app's slots, in the order its toolbar lays them out. */
export type ToolbarSlot = 'color' | 'add' | 'size' | 'clone' | 'rotate' | 'flip' | 'remove' | 'replace' | 'more' | 'unhide';
export const SLOT_ORDER: readonly ToolbarSlot[] = ['color', 'add', 'size', 'clone', 'rotate', 'flip', 'remove', 'replace', 'more', 'unhide'];

export interface ToolbarMenuEntry { id: string; label: string; icon: IconName; run: () => void; checked?: boolean }
export interface ToolbarEntry {
  slot: ToolbarSlot;
  label: string;
  icon?: IconName;
  /** A button. */
  run?: () => void;
  /** A menu instead, with the heading it opens under. */
  menu?: ToolbarMenuEntry[];
  heading?: { title: string; detail: string };
  /** The current finish, drawn in place of an icon. */
  swatch?: { material?: Surface; previewUrl?: string };
  danger?: boolean;
  pressed?: boolean;
  /** Shown but off, with the reason said beside it. */
  disabled?: string;
}
/** A text button under the object. */
/** A text button under the object; `locked` is the app's round black unlock button (`ContextToolbar` `_lockToolbar`). */
export interface ToolbarPill { id: string; label: string; run: () => void; locked?: boolean }
export interface ContextToolbarModel { entries: ToolbarEntry[]; pills: ToolbarPill[] }

/** What the view offers, beyond the store: where the selection was picked on the plan. */
export interface ToolbarContext { planPoint?: () => Vec2 | null }

/** The editor state without its frame statistics, which change every frame and decide nothing here. */
export type ToolbarState = Omit<EditorState, 'stats'>;
type View = 'floor' | 'ceiling' | 'wall' | 'scene';
const viewOf = (state: Pick<EditorState, 'mode' | 'planSurface'>): View => state.mode === 'scene' ? 'scene' : state.planSurface;
const report = (error: string | null) => { if (error) store.set({ error }); };
const openProperties = () => store.set({ showProperties: true });
/** The inspector's own replacement list: models for an item, the fitting list for a door or a window. */
const openReplacement = () => store.set({ showVariations: true });
const metres = (value: number) => value.toFixed(2);
const textured = (surface?: Surface) => !!(surface?.texture || surface?.customTexture);
const turned = (surface: Surface): Surface => ({ ...surface, rotation: ((surface.rotation ?? 0) + 90) % 360 });

export function contextToolbar(state: ToolbarState, level: Level | undefined, context: ToolbarContext = {}): ContextToolbarModel {
  if (!state.selection || !level) return { entries: [], pills: [] };
  const model = contextToolbarOf(state.selection, state, level, context);
  return state.edit === 'items' && state.selection.kind !== 'item' ? itemsOnlyToolbar(model) : model;
}

function contextToolbarOf(selection: NonNullable<Selection>, state: ToolbarState, level: Level, context: ToolbarContext): ContextToolbarModel {
  const empty = { entries: [], pills: [] };
  switch (selection.kind) {
    case 'item': { const item = level.items.find(value => value.id === selection.id); return item ? itemToolbar(state, item) : empty; }
    case 'opening': { const opening = level.openings.find(value => value.id === selection.id); return opening ? openingToolbar(state, level, opening) : empty; }
    case 'wall': { const wall = level.walls.find(value => value.id === selection.id); return wall ? wallToolbar(state, level, wall, selection.side, context) : empty; }
    case 'room': { const room = level.rooms.find(value => value.index === selection.index); return room ? roomToolbar(state, level, room, context) : empty; }
    case 'vertex': return { entries: [{ slot: 'remove', label: t('#ToolbarDeletePoint'), icon: 'trash', danger: true, run: () => store.removeSelection() }], pills: [] };
    case 'trim': { const wall = level.walls.find(value => value.id === selection.wallId); return wall?.[selection.trim] ? trimToolbar(wall, selection.trim) : empty; }
    default: return empty;
  }
}

/** The app's tag rules for furniture (`FurnitureInfo`: IsAlive, IsObject2D, AllowFlip, the replacement's IsExcludedKind). */
export function furnitureRules(item: Pick<Item, 'tags' | 'modelRef'>) {
  const tags = item.tags ?? [], has = (tag: string) => tags.includes(tag);
  const alive = (has('People') || has('Dog') || has('Cat')) && !has('AnimalFurniture');
  const flat = has('Objects2D') && !has('AbstractPicture') && !has('AbstractPainting');
  return {
    /** People, pets and flat symbols have no finish to choose (FurnitureColors). */
    color: !alive && !flat,
    /** Mirroring is for the models made for it: a corner sofa, a tagged model, a flat symbol (Flip). */
    flip: has('CornerSofa') || has('AllowFlip') || has('Objects2D'),
    /** There is something like it to choose from: not a label, a plinth, a wall piece or a panel of the room. */
    replace: tags.length > 0 && !has('Annotation') && !has('Plinth') && !has('CeilingMolding') && !has('InteriorWall') && !isRegion(item as Item),
  };
}

/** How far off a coordinate's axis a wall may run (about 0.06°) for that coordinate to still count as along it. */
const ACROSS_WALL = 1e-3;

/**
 * What a shared project leaves to the app for an item: size, mirror, model and height; turning on a wall. `x`/`z`: a
 * coordinate that does not run along the wall a piece hangs on, which typed alone would take it off its wall — on a wall
 * at an angle, both.
 */
export function sharedItemLimits(edit: EditScope, item?: Pick<Item, 'placement'>, wall?: Pick<Wall, 'dir'>): { props?: string; turn?: string; x?: string; z?: string } {
  if (edit !== 'items') return {};
  const onWall = item?.placement === 'wall', across = onWall && wall ? t('#WebLiveAcrossWall') : undefined;
  return { props: t('#WebLivePropsInApp'), turn: onWall ? t('#WebLiveWallRotateInApp') : undefined,
    x: wall && Math.abs(wall.dir.z) >= ACROSS_WALL ? across : undefined, z: wall && Math.abs(wall.dir.x) >= ACROSS_WALL ? across : undefined };
}

const KEPT_IN_SHARED_PROJECT: ReadonlySet<string> = new Set(['wall-plan', 'scene', 'plan', 'window-view']);
const KEPT_PILLS: ReadonlySet<string> = new Set(['enter-room', 'zoom']);

/**
 * A wall, an opening, a room or a trim in a shared project, where only items change: the views, the catalog lists that add
 * items (stairs are architecture) and the read-only sizes stay; the rest is shown off with the reason.
 */
function itemsOnlyToolbar(model: ContextToolbarModel): ContextToolbarModel {
  const reason = t('#WebLiveItemsOnly');
  const entries = model.entries.flatMap((entry): ToolbarEntry[] => {
    if (entry.slot === 'more') {
      const menu = entry.menu?.filter(row => KEPT_IN_SHARED_PROJECT.has(row.id)) ?? [];
      return menu.length ? [{ ...entry, menu }] : [];
    }
    if (entry.slot === 'add') {
      if (!entry.menu) return [entry];
      const menu = entry.menu.filter(row => ITEM_ADD_LISTS.has(row.id));
      return [menu.length ? { ...entry, menu } : { ...entry, menu: undefined, run: undefined, disabled: reason }];
    }
    if (entry.slot === 'size' && entry.pressed === undefined) return [entry];
    return [{ ...entry, menu: undefined, run: undefined, disabled: reason }];
  });
  return { entries, pills: model.pills.filter(pill => KEPT_PILLS.has(pill.id)) };
}

function itemToolbar(state: ToolbarState, item: Item): ContextToolbarModel {
  const rules = furnitureRules(item);
  const { props } = sharedItemLimits(state.edit), locked = item.locked ? t('#WebItemLocked') : undefined;
  const slot = state.selectionMaterialSlots.find(value => value.key === state.selectionMaterialSlot) ?? state.selectionMaterialSlots[0];
  const slotKey = state.selectionMaterialSlot ?? slot?.key ?? Object.keys(item.materials ?? {})[0] ?? '*';
  const mirror = () => store.commitLevel('', level => ({ ...level, items: level.items.map(target => target.id === item.id
    ? { ...target, mirrored: !target.mirrored, scale: [-target.scale[0], target.scale[1], target.scale[2]] as Item['scale'] } : target) }));
  const entries: ToolbarEntry[] = [
    ...(rules.color ? [{ slot: 'color' as const, label: t('#ToolbarColorAndMaterial'), swatch: { material: slot?.surface ?? item.materials?.[slotKey] ?? item.materials?.['*'], previewUrl: slot?.previewUrl },
      run: () => store.set({ materialTarget: { kind: 'item', itemId: item.id, slot: slotKey } }) }] : []),
    { slot: 'size', label: t('Change Size'), icon: 'ruler', pressed: state.resizeMode, run: () => store.setResizeMode(!store.get().resizeMode), disabled: locked ?? props },
    { slot: 'clone', label: t('#ToolbarDuplicateItem'), icon: 'copy', run: () => { const anchor = store.get().selectionAnchor; store.duplicateSelection(); store.set({ selectionAnchor: anchor }); } },
    // Turning furniture has no button in the app's row (RotateFurniture is never shown): the handle beside it turns it.
    ...(rules.flip ? [{ slot: 'flip' as const, label: t('Flip'), icon: 'mirror' as const, run: mirror, disabled: props }] : []),
    { slot: 'remove', label: t('#DeleteItem'), icon: 'trash', danger: true, run: () => store.removeSelection(), disabled: locked },
    ...(rules.replace ? [{ slot: 'replace' as const, label: t('#ToolbarReplaceItem'), icon: 'replace' as const, run: openReplacement, disabled: props }] : []),
    { slot: 'more', label: t('#ToolbarMoreActions'), icon: 'more', heading: { title: item.caption || t('#ToolbarItem'), detail: t('#ToolbarSizeWdh', metres(item.size.w), metres(item.size.d), metres(item.size.h)) },
      menu: [{ id: 'position', label: item.placement === 'wall' ? t('Position') : t('#PositionAndRotation'), icon: 'sliders', run: openProperties },
        // The app's «⋯» has Lock (MorePopupCommand); a locked piece is unlocked by its round button under it.
        ...(item.locked ? [] : [{ id: 'lock', label: t('Lock'), icon: 'padlock' as const, run: () => report(store.toggleLock()) }])] },
  ];
  return { entries, pills: item.locked ? [{ id: 'unlock', label: t('Unlock'), locked: true, run: () => report(store.toggleLock()) }] : [] };
}

/** The rooms either side of an opening, a few centimetres beyond each face of its wall. */
export function roomsAcross(level: Pick<Level, 'rooms' | 'walls'>, opening: Opening): { front?: Room; back?: Room; centre: Vec2; normal: Vec2 } | null {
  const wall = level.walls.find(value => value.id === opening.wallId);
  if (!wall) return null;
  const centre = { x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset };
  const reach = wall.thickness / 2 + .2;
  const inside = (sign: number) => {
    const probe = { x: centre.x + wall.normalIn.x * reach * sign, z: centre.z + wall.normalIn.z * reach * sign };
    return level.rooms.filter(room => room.polygon.length >= 3 && contains(room.polygon, probe)).sort((a, b) => a.area - b.area)[0];
  };
  return { front: inside(1), back: inside(-1), centre, normal: wall.normalIn };
}

/** «Войти в комнату»: the camera goes to the room on the far side of the door from where it looks (EnterRoom). */
function enterThrough(level: Level, opening: Opening): void {
  const across = roomsAcross(level, opening), scene = editorAccess()?.scene3d();
  if (!across?.front || !across.back || !scene) return;
  const pose = scene.cameraPose(), from = pose ? (pose.mode === 'walk' ? pose.position : pose.target) : null;
  const facing = from ? (from.x - across.centre.x) * across.normal.x + (from.z - across.centre.z) * across.normal.z : 0;
  const room = facing > 0 ? across.back : across.front;
  if (store.get().isolatedRoom !== null) store.set({ isolatedRoom: room.index });
  scene.enterThroughDoor(opening.id, room);
  // The app lets the door go as the camera goes through it (WalkRoomNavigation: Selection.Set(null)).
  store.select(null);
}

function openingToolbar(state: ToolbarState, level: Level, opening: Opening): ContextToolbarModel {
  const tags = opening.modelRef.kind === 'none' ? [] : knownModelTags(opening.modelRef.id) ?? [];
  const kind = openingKind(opening, tags), view = viewOf(state);
  const door = kind === 'door', window = kind === 'window';
  // Through the properties, so the door's copy in the next room turns with it (`syncOpeningTwins`).
  const turnRound = () => report(store.applyOpeningProperties(opening.id, { flipped: !opening.flipped }));
  // The app's «Выбери вид» (ReplaceStreetView): in 3D, on a window, a gap, an arch or a glazed door that looks
  // outside (OpeningFurnitureController.SupportsWindowView). The view is the whole project's here.
  const across = roomsAcross(level, opening);
  const outside = !!across && !across.front !== !across.back;
  const view3d = view === 'scene' && outside && !tags.includes('Niche')
    && (window || kind === 'arch' || tags.includes('WithGlass') || tags.includes('Gap'));
  const more: ToolbarMenuEntry[] = [
    ...(door || window ? [{ id: 'type', label: door ? t('#DoorType') : t('#WindowType'), icon: door ? 'door' as const : 'window' as const, run: openReplacement }] : []),
    ...(view3d ? [{ id: 'window-view', label: t('#ToolbarWindowView'), icon: 'landscape' as const, run: () => store.openWindowViews() }] : []),
    // Curtains are chosen in 3D and on the wall plan, for a window, a door or an arch (AddCurtains, CanAddCurtains).
    ...((view === 'scene' || view === 'wall') && takesCurtains(opening, tags)
      ? [{ id: 'curtains', label: t('#CurtainsChangeButton'), icon: 'curtains' as const, run: () => store.chooseCurtains(opening.id) }] : []),
    // The app keeps «Положение» of an opening off the floor plan (PositionRotationProperties).
    ...(view !== 'floor' && view !== 'ceiling' ? [{ id: 'position', label: t('Position'), icon: 'sliders' as const, run: openProperties }] : []),
  ];
  // A door's or window's colours, part by part, as a piece of furniture's (the app's ItemColor → FurnitureColors):
  // where its model stands in view — in 3D and on the wall plan — and its parts can be told apart.
  const colour = paintableOpening(opening, tags) && (view === 'scene' || view === 'wall');
  const slot = state.selectionMaterialSlots.find(value => value.key === state.selectionMaterialSlot) ?? state.selectionMaterialSlots[0];
  const slotKey = state.selectionMaterialSlot ?? slot?.key ?? '*';
  const entries: ToolbarEntry[] = [
    ...(colour ? [{ slot: 'color' as const, label: t('#ToolbarColorAndMaterial'), swatch: { material: opening.materials?.[slotKey] ?? slot?.surface ?? opening.materials?.['*'], previewUrl: opening.materials?.[slotKey] ? undefined : slot?.previewUrl },
      run: () => store.set({ materialTarget: { kind: 'opening', openingId: opening.id, slot: slotKey } }) }] : []),
    // In 3D and on the wall plan the size button is the app's EnterResizeMode, which an opening shares with furniture:
    // its edges get handles. The floor plan keeps the numbers: the opening's width handles are there already.
    view === 'scene' || view === 'wall'
      ? { slot: 'size', label: t('Change Size'), icon: 'ruler', pressed: state.resizeMode, run: () => store.setResizeMode(!store.get().resizeMode) }
      : { slot: 'size', label: t('#ToolbarOpeningSize'), icon: 'ruler', run: openProperties },
    { slot: 'clone', label: t('#ToolbarDuplicateOpening'), icon: 'copy', run: () => report(store.duplicateOpening(opening.id)) },
    // A door's turn is its swing side (DoorDirection); a garage door has none.
    ...(door && !tags.includes('GarageDoor') ? [{ slot: 'rotate' as const, label: t('#ToolbarSwingSide'), icon: 'rotate' as const, run: turnRound }] : []),
    ...(window && tags.includes('AllowFlip') ? [{ slot: 'flip' as const, label: t('Flip'), icon: 'mirror' as const, run: turnRound }] : []),
    { slot: 'remove', label: t('#ToolbarDeleteOpening'), icon: 'trash', danger: true, run: () => store.removeSelection() },
    ...(door || window ? [{ slot: 'replace' as const, label: door ? t('#ToolbarReplaceDoor') : t('#ToolbarReplaceWindow'), icon: 'replace' as const, run: openReplacement }] : []),
    ...(more.length ? [{ slot: 'more' as const, label: t('#ToolbarMoreOpeningActions'), icon: 'more' as const,
      heading: { title: opening.caption || (door ? t('Door') : window ? t('Window') : t('#ToolbarOpening')), detail: t('#ToolbarSizeWh', metres(opening.width), metres(opening.height)) }, menu: more }] : []),
  ];
  const pills: ToolbarPill[] = view === 'scene' && (door || kind === 'arch') && across?.front && across.back && across.front !== across.back
    ? [{ id: 'enter-room', label: t('#EnterRoom'), run: () => enterThrough(level, opening) }] : [];
  return { entries, pills };
}

function wallToolbar(state: ToolbarState, level: Level, wall: Wall, side: 'front' | 'back', context: ToolbarContext): ContextToolbarModel {
  const view = viewOf(state);
  const size: ToolbarEntry = { slot: 'size', label: t('#ToolbarWallSize'), icon: 'ruler', run: openProperties };
  const remove: ToolbarEntry = { slot: 'remove', label: t('#RemoveWall'), icon: 'trash', danger: true, run: () => store.removeSelection() };
  const contour = isContourWall(level, wall);
  // On the plan a hidden wall stays selected, dashed, with «Показать стену» at hand; in 3D it is gone from view, and so is the selection.
  const hide: ToolbarMenuEntry = { id: 'hide', label: t('#HideWall'), icon: 'eyeOff', run: () => { store.setWallHidden(wall.id, true); if (view !== 'floor' && view !== 'ceiling') store.select(null); } };
  const heading = { title: t('Wall'), detail: t('#ToolbarSizeWh', metres(wall.length), metres(wall.height)) };
  if (view === 'floor' || view === 'ceiling') {
    if (wall.hidden) {
      const fill = (kind: 'door' | 'window' | 'arch') => () => report(store.fillHiddenWall(wall.id, kind));
      return { pills: [], entries: [
        ...(wall.length <= HIDDEN_WALL_OPENING_MAX ? [{ slot: 'add' as const, label: t('#ToolbarOpeningInsteadOfWall'), icon: 'plus' as const, menu: [
          { id: 'door', label: t('Add Door'), icon: 'door' as const, run: fill('door') },
          { id: 'window', label: t('Add Window'), icon: 'window' as const, run: fill('window') },
          { id: 'arch', label: t('#AddArch'), icon: 'arch' as const, run: fill('arch') },
        ] }] : []),
        size, remove,
        { slot: 'unhide', label: t('#ShowWall'), icon: 'eye', run: () => store.setWallHidden(wall.id, false) },
      ] };
    }
    const add = (kind: 'door' | 'window' | 'arch') => () => report(store.addOpening(wall.id, kind));
    return { pills: [], entries: [
      { slot: 'add', label: t('#ToolbarAddToWall'), icon: 'plus', menu: [
        { id: 'split', label: t('#SplitWall'), icon: 'splitWall', run: () => report(store.splitWallAt(wall.id, context.planPoint?.() ?? null)) },
        { id: 'window', label: t('Add Window'), icon: 'window', run: add('window') },
        { id: 'door', label: t('Add Door'), icon: 'door', run: add('door') },
        { id: 'arch', label: t('#ToolbarAddOpening'), icon: 'arch', run: add('arch') },
      ] },
      size, remove,
      ...(contour ? [{ slot: 'more' as const, label: t('#ToolbarMoreWallActions'), icon: 'more' as const, heading, menu: [hide] }] : []),
    ] };
  }
  // Picked by its face, in 3D and on the wall plan: the app's WallSideController.
  const key = side === 'front' ? 'frontSurface' : 'backSurface';
  const surface = wall[key] ?? (side === 'back' ? wall.frontSurface : undefined), target = { kind: 'wall' as const, wallId: wall.id, side };
  const face = !wall.hidden && isInteriorSide(level, wall, side);
  const more: ToolbarMenuEntry[] = [
    ...(view === 'scene' && face ? [{ id: 'wall-plan', label: t('#GoTo2D'), icon: 'wall' as const, run: () => { store.select(null); store.set({ mode: 'plan', wallMode: 'full', planSurface: 'wall', wallView: { wallId: wall.id, side } }); } }] : []),
    ...(view === 'wall' ? [{ id: 'scene', label: t('#GoTo3D'), icon: 'cube' as const, run: () => { store.select(null); store.set({ mode: 'scene' }); } }] : []),
    // The web draws a plinth on the wall's room face only (`normalIn`, the front).
    ...(side === 'front' && face ? [{ id: 'plinth', label: wall.plinth ? t('#ToolbarRemovePlinth') : t('#AddPlinths'), icon: 'plinth' as const, run: () => store.toggleWallPlinth(wall.id) },
      { id: 'molding', label: wall.molding ? t('#ToolbarRemoveMolding') : t('#AddMolding'), icon: 'molding' as const, run: () => store.toggleWallMolding(wall.id) }] : []),
    ...(contour ? [hide] : []),
  ];
  // «Добавить молдинг» and «Добавить мансарду» of the app's button on the top of a wall side in 3D (AddAtticHelper,
  // ShowAddAtticPopup), under the bar here; the web draws a molding on the room face (`normalIn`) only.
  const sign = side === 'front' ? 1 : -1;
  const pills: ToolbarPill[] = view === 'scene' && face && takesAttic(level, wall, sign) ? [
    ...(side === 'front' ? [{ id: 'molding', label: t('#AddMolding'), run: () => store.toggleWallMolding(wall.id) }] : []),
    { id: 'attic', label: t('#AddAttic'), run: () => addAttic(wall.id, sign) },
  ] : [];
  return { pills, entries: [
    { slot: 'color', label: t('#ToolbarWallSideMaterial'), swatch: { material: surface }, run: () => store.set({ materialTarget: target }) },
    { slot: 'add', label: t('#AddNewItem'), icon: 'plus', run: () => store.set({ catalogSection: null, showCatalog: true }) },
    size,
    ...(textured(surface) ? [{ slot: 'rotate' as const, label: t('#ToolbarRotateTexture'), icon: 'rotate' as const, run: () => store.setSurface(target, turned(surface!)) }] : []),
    ...(isFreeStandingWall(level, wall) ? [remove] : []),
    ...(more.length ? [{ slot: 'more' as const, label: t('#ToolbarMoreWallActions'), icon: 'more' as const, heading, menu: more }] : []),
  ] };
}

/**
 * A plinth or a ceiling molding picked by itself (the app's WallPlinthController): its colour
 * (ReplacePlinthOrCeilingMoldingColor), its height (the size panel shows only that) and «Убрать» (RemovePlinth) — no copy,
 * replacement or «⋯», as the app has none for it.
 */
function trimToolbar(wall: Wall, trim: Trim): ContextToolbarModel {
  const plinth = trim === 'plinth';
  return { pills: [], entries: [
    { slot: 'color', label: t('#ToolbarColorAndMaterial'), swatch: { material: wall[trim]?.surface }, run: () => store.set({ materialTarget: { kind: 'trim', wallId: wall.id, trim } }) },
    { slot: 'size', label: plinth ? t('#ToolbarPlinthHeight') : t('#ToolbarMoldingHeight'), icon: 'ruler', run: openProperties },
    { slot: 'remove', label: plinth ? t('#ToolbarRemovePlinth') : t('#ToolbarRemoveMolding'), icon: 'trash', danger: true, run: () => store.removeSelection() },
  ] };
}

/** The lists the app's room «+» opens in the catalog (AddFurnitureToRoom2D), by the tree's own ids. */
export const ROOM_ADD_LISTS = [
  { id: 'columns', get label() { return t('#ColumnsAndBeams'); }, icon: 'room', section: 'Build/Constructions/Columns' },
  { id: 'stairs', get label() { return t('#StairsAndPodiums'); }, icon: 'layers', section: 'Build/Stairs' },
  { id: 'dividers', get label() { return t('#PartitionsAndZoning'); }, icon: 'wall', section: 'Storage/Room divider' },
  { id: 'labels', get label() { return t('#LabelsAndAnnotations'); }, icon: 'pencil', section: 'Special/Annotations' },
  { id: 'other', get label() { return t('#Other'); }, icon: 'more', section: null },
] as const;
/** And the ceiling's (AddFurnitureToCeiling): the lights and the beams and constructions. */
export const CEILING_ADD_LISTS = [
  { id: 'lights', get label() { return t('#ToolbarCeilingLights'); }, icon: 'sun', section: 'Lighting/Ceiling Lighting' },
  { id: 'constructions', get label() { return t('#ToolbarCeilingBeams'); }, icon: 'ceiling', section: 'Build/Constructions/CeilingDecor' },
] as const;
/** The «+» lists a shared project keeps: they add items, where stairs are architecture. */
const ITEM_ADD_LISTS: ReadonlySet<string> = new Set([...ROOM_ADD_LISTS, ...CEILING_ADD_LISTS].map(list => list.id).filter(id => id !== 'stairs'));

/** «Выбери тип комнаты» under a room while its type says nothing, or while another room has the same one. */
export function asksRoomType(level: Pick<Level, 'rooms'>, room: Room): boolean {
  return !hasRoomType(room.roomType) || level.rooms.filter(other => other.roomType.trim().toLowerCase() === room.roomType.trim().toLowerCase()).length > 1;
}

function roomToolbar(state: ToolbarState, level: Level, room: Room, context: ToolbarContext): ContextToolbarModel {
  const view = viewOf(state), ceiling = view === 'ceiling';
  const surface = ceiling ? room.ceilingSurface : room.floorSurface;
  const target = { kind: ceiling ? 'ceiling' as const : 'floor' as const, roomIndex: room.index };
  const catalog = (section: string | null) => () => store.set({ catalogSection: section, showCatalog: true });
  const lists = view === 'floor' ? ROOM_ADD_LISTS : ceiling ? CEILING_ADD_LISTS : [];
  // The ceiling's «Добавить наклонный потолок» (AddFurnitureToCeiling.AddSloppedCeiling), second as in the app: a mansard
  // along the room's wall nearest to where the ceiling was picked, which has none yet.
  const slope = ceiling && nearestAtticWall(level, room.index, context.planPoint?.() ?? null)
    ? [{ id: 'attic', label: t('#ToolbarSlopedCeiling'), icon: 'ceiling' as const, run: () => {
      const face = nearestAtticWall(level, room.index, context.planPoint?.() ?? null);
      if (face) addAttic(face.wall.id, face.side);
    } }] : [];
  const plinths = view !== 'ceiling' && roomPlinthWalls(level, room.index).length > 0;
  // A ceiling's «Добавить молдинг» (AddPlinthsOrMoldings for the CeilingController): moldings along the top of every wall.
  const moldings = ceiling && roomPlinthWalls(level, room.index).length > 0;
  const more: ToolbarMenuEntry[] = [
    ...(view === 'scene' ? [{ id: 'plan', label: t('#GoTo2D'), icon: 'floor' as const, run: () => { store.select(null); store.set({ mode: 'plan', wallMode: 'full', planSurface: 'floor' }); } }] : []),
    ...(view !== 'scene' && showsIn3D(level) ? [{ id: 'scene', label: t('#GoTo3D'), icon: 'cube' as const, run: () => { store.select(null); store.set({ mode: 'scene' }); } }] : []),
    ...(plinths ? [{ id: 'plinths', label: roomHasPlinths(level, room.index) ? t('#ToolbarRemovePlinths') : t('#AddPlinths'), icon: 'plinth' as const, run: () => store.toggleRoomPlinths(room.index) }] : []),
    ...(moldings ? [{ id: 'moldings', label: roomHasTrims(level, room.index, 'molding') ? t('#ToolbarRemoveMoldings') : t('#ToolbarAddMoldings'), icon: 'molding' as const, run: () => store.toggleRoomMoldings(room.index) }] : []),
    ...(view !== 'scene' ? [{ id: 'type', label: t('#RoomTypeText'), icon: 'room' as const, run: openProperties }] : []),
  ];
  const entries: ToolbarEntry[] = [
    { slot: 'color', label: ceiling ? t('#ToolbarCeilingFinish') : t('#ToolbarFloorFinish'), swatch: { material: surface }, run: () => store.set({ materialTarget: target }) },
    ...(lists.length ? [{ slot: 'add' as const, label: ceiling ? t('#ToolbarAddToCeiling') : t('#AddToRoom'), icon: 'plus' as const,
      menu: lists.flatMap((list, index) => [...(index === 1 ? slope : []), { id: list.id, label: list.label, icon: list.icon, run: catalog(list.section) }]) }] : []),
    // «Размер» (the app's ResizeRoom → InfoRoomSizePopup): the room's width, length, area, ceiling and walls in the inspector.
    { slot: 'size' as const, label: t('#ToolbarRoomSize'), icon: 'ruler' as const, run: openProperties },
    // The app's CloneRoom and RemoveRoom, on the floor plan only (`EditorHelper.IsFloor2D`); each asks first.
    ...(view === 'floor' ? [{ slot: 'clone' as const, label: t('#ToolbarCloneRoom'), icon: 'copy' as const, run: () => store.askRoomAction('clone') }] : []),
    ...(textured(surface) ? [{ slot: 'rotate' as const, label: t('#ToolbarRotateTexture'), icon: 'rotate' as const, run: () => store.setSurface(target, turned(surface!)) }] : []),
    ...(view === 'floor' ? [{ slot: 'remove' as const, label: t('#ToolbarDeleteRoom'), icon: 'trash' as const, danger: true, run: () => store.askRoomAction('remove') }] : []),
    ...(more.length ? [{ slot: 'more' as const, label: t('#ToolbarMoreRoomActions'), icon: 'more' as const,
      heading: { title: room.name || t('#PropsRoom'), detail: `${t('#ValueSquareMeters', floorArea(level, room).toFixed(1))} · ${t(ceiling ? 'Ceiling' : 'Floor')}` }, menu: more }] : []),
  ];
  const pills: ToolbarPill[] = view === 'floor' || ceiling ? [
    ...(asksRoomType(level, room) ? [{ id: 'room-type', label: t('#RoomTypeText'), run: openProperties }] : []),
    ...(view === 'floor' && level.rooms.length > 1 ? [{ id: 'zoom', label: t('#ZoomToRoom'), run: () => zoomTo(room) }] : []),
  ] : [];
  return { entries, pills };
}

/**
 * «Добавить мансарду»: the store adds it, selected in its resize; once the inspector has opened for it, the view
 * brings it out from under the panels, as a model placed from the catalog is brought (`EditorView.placeItem`).
 */
function addAttic(wallId: string, side: 1 | -1): void {
  const error = store.addAttic(wallId, side);
  if (error) { report(error); return; }
  const id = store.get().selection?.kind === 'item' ? (store.get().selection as { id: string }).id : null;
  if (typeof requestAnimationFrame === 'undefined') return;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const access = editorAccess(), level = store.level, item = level?.items.find(value => value.id === id);
    if (!access || !level || !item || store.get().selection?.kind !== 'item') return;
    const frame = atticFrame(item, level.walls), middle = atticPoint(frame, 0, frame.depth / 2);
    const moved = store.get().mode === 'scene' ? access.scene3d()?.revealPoint(middle, access.visibleArea(), frame.knee + frame.height / 2)
      : access.plan()?.revealPoint(middle, access.visibleArea());
    if (moved) store.set({ selectionAnchor: null });
  }));
}

/** «Приблизить» on the plan (EnterRoom's #ZoomToRoom): the room in the middle, filling the view, as the minimap's rooms go. */
function zoomTo(room: Room): void {
  const xs = room.polygon.map(p => p.x), zs = room.polygon.map(p => p.z);
  const extent = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...zs) - Math.min(...zs)) * 1.3;
  window.dispatchEvent(new CustomEvent('hp:pan-plan', { detail: { centre: labelCenter(room.polygon), extent } }));
}
